import { ReactElement } from 'react'
import { Box, Card, Chip, LinearProgress, Typography } from '@mui/material'
import { ChevronRight } from '@mui/icons-material'
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { CalendarIcon, IncorrectIcon, CorrectIcon } from 'src/v3/core/icons'
import { AssignmentWithCourses } from '../types'
import {
  getAssignmentStatus,
  getDueDateContext,
  getCompletedCourseCount,
  getTotalCourseCount,
  getProgressPercentage,
} from '../utils/helpers'
import AssignmentThumbnailGrid from './assignment-thumbnail-grid'

type AssignmentCardProps = {
  assignment: AssignmentWithCourses
  linkedServiceName?: string
}

export default function AssignmentCard({
  assignment,
  linkedServiceName,
}: AssignmentCardProps): ReactElement {
  let navigate = useNavigate()
  let status = getAssignmentStatus(assignment)
  let dueDateContext = getDueDateContext(assignment)
  let completedCount = getCompletedCourseCount(assignment)
  let totalCount = getTotalCourseCount(assignment)
  let progressPercentage = getProgressPercentage(assignment)

  let statusConfig = {
    Active: {
      color: 'primary' as const,
      icon: <PlayCircleOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    Overdue: {
      color: 'error' as const,
      icon: <IncorrectIcon sx={{ fontSize: 16 }} />,
    },
    Completed: {
      color: 'success' as const,
      icon: <CorrectIcon sx={{ fontSize: 16 }} />,
    },
  }

  let config = statusConfig[status]

  let handleClick = () => {
    navigate(`/assignments/${assignment.id}`)
  }

  return (
    <Card
      sx={{
        p: 3,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.main',
          transform: 'translateY(-2px)',
        },
      }}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
      aria-label={`View ${assignment.name} assignment`}
    >
      <Box className="flex items-start gap-3">
        {/* Course Thumbnails */}
        <Box sx={{ width: 120, flexShrink: 0 }}>
          <AssignmentThumbnailGrid courses={assignment.courses} />
        </Box>

        <Box className="flex-1 min-w-0">
          {/* Assignment Name */}
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {assignment.name}
          </Typography>

          {/* Status and Due Date Context */}
          <Box className="flex flex-wrap items-center gap-2 mb-2">
            <Chip
              size="small"
              label={status}
              icon={config.icon}
              aria-label={`Status: ${status}`}
              sx={{
                color: config.color,
              }}
            />
            {assignment.dueDate && (
              <Box className="flex items-center gap-1">
                <CalendarIcon
                  sx={{ fontSize: 16, color: 'text.secondary' }}
                  aria-hidden="true"
                />
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      status === 'Overdue' ? 'error.main' : 'text.secondary',
                    fontWeight: status === 'Overdue' ? 600 : 400,
                  }}
                >
                  Due {dayjs(assignment.dueDate).format('MMM D, YYYY')}
                  {dueDateContext && ` • ${dueDateContext}`}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Linked Service */}
          {linkedServiceName && (
            <Typography
              color="text.secondary"
              variant="caption"
              className="block mb-2"
            >
              Assigned by {linkedServiceName}
            </Typography>
          )}

          {/* Course Progress */}
          <Box className="mt-3">
            <Box className="flex items-center justify-between mb-1">
              <Typography color="text.secondary" variant="body2">
                {totalCount} course{totalCount === 1 ? '' : 's'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {completedCount} of {totalCount} completed
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                color: status === 'Completed' ? 'success' : 'primary',
                height: 8,
                borderRadius: 4,
              }}
              aria-label={`Assignment progress: ${completedCount} of ${totalCount} courses completed, ${progressPercentage} percent`}
            />
          </Box>
        </Box>

        {/* Chevron Icon */}
        <ChevronRight
          sx={{ color: 'text.secondary', mt: 0.5 }}
          aria-hidden="true"
        />
      </Box>
    </Card>
  )
}
